import React from "react";
import { Link } from "react-router-dom";
import {
  Heart,
  BookOpen,
  Utensils,
  Stethoscope,
  Home,
  GraduationCap,
  Users,
  Activity,
  ArrowRight,
} from "lucide-react";

// Import images
import winterClothe from "../../assets/image/winter-clothe.jpeg";
import food from "../../assets/image/food.jpeg";
import medical from "../../assets/image/medical.jpeg"; // You'll need to add this image
import education from "../../assets/image/education.jpg";
import housing from "../../assets/image/housing.jpeg"; // You'll need to add this image
import educational_support from "../../assets/image/educational-support.jpeg"; // You'll need to add this image
import empowerment from "../../assets/image/empowerment.jpeg"; // You'll need to add this image
import medical_camp from "../../assets/image/medical-camp.jpeg"; // You'll need to add this image

const ActivityCard = ({ activity, index }) => {
  // Icons for each activity
  const icons = [
    <Heart className="text-white" size={28} />,
    <BookOpen className="text-white" size={28} />,
    <Utensils className="text-white" size={28} />,
    <Stethoscope className="text-white" size={28} />,
    <Home className="text-white" size={28} />,
    <GraduationCap className="text-white" size={28} />,
    <Users className="text-white" size={28} />,
    <Activity className="text-white" size={28} />,
  ];

  // Background colors for the icons
  const iconBgColors = [
    "bg-rose-600",
    "bg-blue-600",
    "bg-amber-600",
    "bg-emerald-600",
    "bg-purple-600",
    "bg-indigo-600",
    "bg-teal-600",
    "bg-orange-600",
  ];

  // Descriptions for each activity
  const descriptions = [
    "শীতার্ত মানুষের জন্য উষ্ণ বস্ত্র বিতরণ করে আমরা সহায়তা করি।",
    "দরিদ্র মেধাবী শিক্ষার্থীদের শিক্ষা বৃত্তি এবং সহায়তা প্রদান।",
    "দুর্যোগ এবং দারিদ্র্য পীড়িতদের মাঝে খাদ্য সামগ্রী বিতরণ।",
    "স্বাস্থ্যসেবা নিশ্চিত করতে বিনামূল্যে চিকিৎসা সহায়তা প্রদান।",
    "গৃহহীন ও দুস্থ পরিবারগুলোর জন্য বাসস্থান নির্মাণ ও সহায়তা।",
    "সকল শ্রেণীর শিক্ষার্থীদের জন্য শিক্ষা উপকরণ ও সহায়তা প্রদান।",
    "দরিদ্র জনগোষ্ঠীর অর্থনৈতিক সাবলম্বীকরণে প্রশিক্ষণ ও সহায়তা।",
    "বিনামূল্যে স্বাস্থ্য পরীক্ষা ও রক্তগ্রুপ নির্ণয় ক্যাম্প আয়োজন।",
  ];

  return (
    <div
      data-aos="zoom-in-up"
      data-aos-delay={index * 100}
      className="group relative rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300"
    >
      <div className="absolute -top-12 group-hover:-top-6 right-0 transition-all duration-500 opacity-20 group-hover:opacity-30 -rotate-12 group-hover:rotate-0">
        {icons[index]}
      </div>

      {/* Image with overlay */}
      <div className="relative h-52 overflow-hidden">
        <img
          src={activity.image}
          alt={activity.name}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-black/20"></div>

        {/* Icon badge */}
        <div
          className={`absolute top-4 left-4 ${iconBgColors[index]} p-2 rounded-full transition-transform duration-300 group-hover:scale-110`}
        >
          {icons[index]}
        </div>
      </div>

      {/* Content */}
      <div className="p-5 bg-white">
        <h3 className="text-xl font-bold text-gray-800 mb-2">
          {activity.name}
        </h3>
        <p className="text-gray-600 mb-4 text-sm">{descriptions[index]}</p>

        <Link
          to="/donation"
          className="inline-flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-white py-2 px-4 rounded-lg transition-all duration-300 w-full"
        >
          <span>অনুদান করুন</span>
          <ArrowRight size={16} />
        </Link>
      </div>
    </div>
  );
};

const HomeBanner = () => {
  const activities = [
    { id: 1, name: "শীতবস্ত্র বিতরণ", image: winterClothe },
    { id: 2, name: "শিক্ষা বৃত্তি এবং সহায়তা", image: education },
    { id: 3, name: "খাদ্য সহায়তা", image: food },
    { id: 4, name: "চিকিৎসা সহায়তা", image: medical },
    { id: 5, name: "বাসস্থান সহায়তা", image: housing },
    { id: 6, name: "শিক্ষা সহায়তা", image: educational_support },
    { id: 7, name: "সাবলম্বীকরণ প্রজেক্ট", image: empowerment },
    {
      id: 8,
      name: "ফ্রি মেডিকেল ও রক্তগ্রুপ নির্ণয় ক্যাম্প",
      image: medical_camp,
    },
  ];

  return (
    <section className="mt-20px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
      <div className="text-center mb-12">
        <span className="text-primary font-semibold">আমাদের উদ্যোগ</span>
        <h2 className="text-3xl sm:text-4xl font-bold text-gray-800 mt-2 mb-4">
          আমাদের কার্যক্রম সমূহ
        </h2>
        <div className="w-24 h-1 bg-primary mx-auto mb-6"></div>
        <p className="max-w-2xl mx-auto text-gray-600">
          আমরা সমাজের অসহায়, নিপীড়িত, হতদরিদ্র, নিঃস্বদের মৌলিক অধিকার ও অনাথ
          পথশিশুদের কল্যাণে নিরন্তর কাজ করে যাচ্ছি। আপনাদের সহযোগিতা আমাদের এই
          প্রচেষ্টাকে আরও অনুপ্রাণিত ও শক্তিশালী করবে।
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
        {activities?.map((activity, index) => (
          <ActivityCard key={activity.id} activity={activity} index={index} />
        ))}
      </div>
    </section>
  );
};

export default HomeBanner;
